import { useState, useEffect } from 'react';
import { X, Bell, ExternalLink, Calendar, Zap, Award, Users, ChevronLeft, ChevronRight, AlertCircle, Info, RefreshCw } from 'lucide-react';

const NotificationBar = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [currentNotification, setCurrentNotification] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUsingFallback, setIsUsingFallback] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Function to determine notification type and styling based on content
  const getNotificationStyle = (text, mediaName) => {
    const lowerText = text.toLowerCase();
    const lowerMedia = mediaName.toLowerCase();

    // Determine type and priority based on keywords
    let type = 'announcement';
    let priority = 'medium';
    let icon = Bell;
    let gradient = 'from-blue-600 via-blue-700 to-purple-600';
    let badge = 'New';

    // Event-related keywords
    if (lowerText.includes('event') || lowerText.includes('webinar') || lowerText.includes('summit') ||
      lowerText.includes('conference') || lowerText.includes('workshop') || lowerMedia.includes('event')) {
      type = 'event';
      icon = Calendar;
      gradient = 'from-emerald-600 via-emerald-700 to-teal-600';
      badge = 'Event';
      priority = 'high';
    }
    // Update/Feature keywords
    else if (lowerText.includes('update') || lowerText.includes('feature') || lowerText.includes('new') ||
      lowerText.includes('launch') || lowerMedia.includes('update')) {
      type = 'feature';
      icon = Zap;
      gradient = 'from-orange-600 via-orange-700 to-red-600';
      badge = 'Update';
      priority = 'medium';
    }
    // Achievement/Milestone keywords
    else if (lowerText.includes('milestone') || lowerText.includes('achievement') || lowerText.includes('celebration') ||
      lowerText.includes('success') || lowerMedia.includes('achievement')) {
      type = 'achievement';
      icon = Award;
      gradient = 'from-pink-600 via-pink-700 to-rose-600';
      badge = 'Achievement';
      priority = 'medium';
    }
    // Community/Social keywords
    else if (lowerText.includes('community') || lowerText.includes('member') || lowerText.includes('join') ||
      lowerMedia.includes('community')) {
      type = 'community';
      icon = Users;
      gradient = 'from-indigo-600 via-indigo-700 to-purple-600';
      badge = 'Community';
      priority = 'medium';
    }
    // Urgent/Important keywords
    else if (lowerText.includes('urgent') || lowerText.includes('important') || lowerText.includes('critical') ||
      lowerText.includes('alert') || lowerMedia.includes('urgent')) {
      type = 'alert';
      icon = AlertCircle;
      gradient = 'from-red-600 via-red-700 to-pink-600';
      badge = 'Urgent';
      priority = 'high';
    }
    // Info/General keywords
    else if (lowerText.includes('info') || lowerText.includes('notice') || lowerMedia.includes('info')) {
      type = 'info';
      icon = Info;
      gradient = 'from-cyan-600 via-cyan-700 to-blue-600';
      badge = 'Info';
      priority = 'low';
    }

    return { type, priority, icon, gradient, badge };
  };

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/notifications`);
      if (!response.ok) {
        throw new Error('Failed to fetch notifications');
      }

      const backendNotifications = await response.json();

      // Transform backend notifications to frontend format
      const transformedNotifications = backendNotifications.map((notification) => {
        const style = getNotificationStyle(notification.text, notification.mediaName);

        return {
          id: notification._id,
          type: style.type,
          icon: style.icon,
          title: notification.mediaName,
          message: notification.text,
          action: 'Visit',
          link: notification.link,
          priority: style.priority,
          gradient: style.gradient,
          badge: style.badge,
          createdAt: notification.createdAt
        };
      });

      // Sort by creation date (newest first) and priority
      const sortedNotifications = transformedNotifications.sort((a, b) => {
        // First sort by priority (high > medium > low)
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        // Then by creation date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setNotifications(sortedNotifications);
      setIsUsingFallback(false);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.message);

      // Fallback to sample notifications if API fails (optional)
      const fallbackNotifications = [
        {
          id: 'fallback-1',
          type: 'announcement',
          icon: Bell,
          title: 'Welcome to Our Platform',
          message: 'Stay tuned for important updates and announcements',
          action: 'Learn More',
          link: '/',
          priority: 'medium',
          gradient: 'from-blue-600 via-blue-700 to-purple-600',
          badge: 'Welcome',
          createdAt: new Date().toISOString()
        }
      ];

      // Only use fallback if there are no existing notifications
      if (notifications.length === 0) {
        setNotifications(fallbackNotifications);
        setIsUsingFallback(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Check if notification was dismissed
  useEffect(() => {
    const dismissed = localStorage.getItem('notificationBarDismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const now = Date.now();
      // Show again after 24 hours
      if (now - dismissedTime < 24 * 60 * 60 * 1000) {
        setIsVisible(false);
      }
    }
  }, []);

  // Auto-rotate notifications every 6 seconds
  useEffect(() => {
    if (notifications.length > 1 && !isPaused) {
      const interval = setInterval(() => {
        changeNotification((prev) => (prev + 1) % notifications.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [notifications.length, isPaused]);

  const changeNotification = (newIndex) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentNotification(newIndex);
      setIsAnimating(false);
    }, 150);
  };

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('notificationBarDismissed', Date.now().toString());
  };

  const handlePrevious = () => {
    const newIndex = currentNotification === 0 ? notifications.length - 1 : currentNotification - 1;
    changeNotification(newIndex);
  };

  const handleNext = () => {
    const newIndex = (currentNotification + 1) % notifications.length;
    changeNotification(newIndex);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && notifications.length > 1) handleNext();
    if (isRightSwipe && notifications.length > 1) handlePrevious();
  };

  const handleAction = (link) => {
    if (link) {
      // Check if it's an external link or internal route
      if (link.startsWith('http') || link.startsWith('https')) {
        window.open(link, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = link;
      }
    }
  };

  // Refresh notifications every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    await fetchNotifications();
  };

  // Don't render if not visible
  if (!isVisible) return null;

  // Show loading state
  if (loading) {
    return (
      <div className="relative z-50">
        <div className="bg-gradient-to-r from-slate-600 to-slate-700 shadow-lg border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-3 min-h-[60px]">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span className="text-white/80 text-sm">Loading notifications...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if error or no notifications
  if (error || notifications.length === 0) return null;

  const notification = notifications[currentNotification];
  const IconComponent = notification.icon;

  return (
    <div className="relative z-50">
      {/* Notification Bar */}
      <div
        className={`bg-gradient-to-r ${notification.gradient} shadow-lg border-b border-white/10 transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
          }`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 min-h-[60px]">
            {/* Left Content */}
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              {/* Icon with animation and priority indicator */}
              <div className="flex-shrink-0">
                <div className="relative">
                  {notification.priority === 'high' && (
                    <div className="absolute inset-0 bg-white/30 rounded-full animate-ping"></div>
                  )}
                  <div className={`relative backdrop-blur-sm rounded-full p-2.5 border transition-all duration-300 ${notification.priority === 'high'
                      ? 'bg-white/20 border-white/30 shadow-lg'
                      : 'bg-white/10 border-white/20'
                    }`}>
                    <IconComponent className={`w-5 h-5 text-white ${notification.priority === 'high' ? 'drop-shadow-sm' : ''
                      }`} />
                  </div>

                  {/* Priority badge */}
                  {notification.badge && (
                    <div className="absolute -top-1 -right-1 bg-white text-xs font-bold px-1.5 py-0.5 rounded-full text-gray-800 shadow-sm">
                      {notification.badge}
                    </div>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
                  <div className={`flex-1 min-w-0 transition-all duration-300 ${isAnimating ? 'opacity-0 transform translate-x-2' : 'opacity-100 transform translate-x-0'
                    }`}>
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-semibold text-white truncate">
                        {notification.title}
                      </p>
                      {notification.priority === 'high' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Priority
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-white/90 line-clamp-1 sm:line-clamp-none">
                      {notification.message}
                    </p>
                  </div>

                  {/* Action Button */}
                  {notification.action && (
                    <div className="mt-2 sm:mt-0 flex-shrink-0">
                      <button
                        onClick={() => handleAction(notification.link)}
                        className={`inline-flex items-center space-x-2 backdrop-blur-sm border rounded-lg px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:scale-105 active:scale-95 shadow-sm ${notification.priority === 'high'
                            ? 'bg-white/20 hover:bg-white/30 border-white/30 font-semibold'
                            : 'bg-white/10 hover:bg-white/20 border-white/20'
                          }`}
                      >
                        <span>{notification.action}</span>
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Content */}
            <div className="flex items-center space-x-3 ml-4">
              {/* Navigation Controls */}
              {notifications.length > 1 && (
                <div className="hidden sm:flex items-center space-x-2">
                  <button
                    onClick={handlePrevious}
                    className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-1 text-white transition-all duration-200 hover:scale-110"
                    aria-label="Previous notification"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>

                  {/* Notification Indicators */}
                  <div className="flex items-center space-x-1 px-2">
                    {notifications.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => changeNotification(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentNotification
                            ? 'bg-white scale-125'
                            : 'bg-white/40 hover:bg-white/60'
                          }`}
                      />
                    ))}
                  </div>

                  <button
                    onClick={handleNext}
                    className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-1 text-white transition-all duration-200 hover:scale-110"
                    aria-label="Next notification"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Mobile notification count */}
              {notifications.length > 1 && (
                <div className="sm:hidden flex items-center space-x-2 text-white/70 text-xs">
                  <span>{currentNotification + 1}/{notifications.length}</span>
                </div>
              )}

              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex-shrink-0 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-1.5 text-white transition-all duration-200 hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Refresh notifications"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>

              {/* Close Button */}
              <button
                onClick={handleClose}
                className="flex-shrink-0 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-1.5 text-white transition-all duration-200 hover:scale-110 active:scale-95 ml-2"
                aria-label="Close notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar for Auto-rotation */}
        {notifications.length > 1 && !isPaused && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20">
            <div
              className="h-full bg-white transition-all duration-100 ease-linear"
              style={{
                width: '100%',
                animation: 'progress 6s linear infinite'
              }}
            />
          </div>
        )}

        {/* Pause indicator */}
        {isPaused && notifications.length > 1 && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/40">
            <div className="absolute right-2 -top-6 text-xs text-white/70 bg-black/20 px-2 py-1 rounded">
              Paused
            </div>
          </div>
        )}
      </div>

      {/* Custom CSS for progress animation */}
      <style jsx>{`
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        
        .notification-enter {
          opacity: 0;
          transform: translateX(20px);
        }
        
        .notification-enter-active {
          opacity: 1;
          transform: translateX(0);
          transition: all 300ms ease-out;
        }
      `}</style>
    </div>
  );
};

export default NotificationBar;